import React, { useMemo } from "react";
import { ROOTS } from "@app/config/constants";
import { Avatar } from "@medentic/polaris-react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { User } from "@shared/types/user";
import { useTranslation } from "react-i18next";
import { HealthcareCompany, HealthcareCompanyType } from "@shared/types/commonTypes";
import { getLocalStorage, setLocalStorage } from "@shared/lib";
import { MedenticTopBar, MenuAction } from "@shared/ui";

type UserTopBarProps = {
  onNavigationToggle: () => void;
};

const EMPTY_CLINICS: HealthcareCompany[] = [];

const UserTopBar: React.FC<UserTopBarProps> = ({ onNavigationToggle }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = queryClient.getQueryData<User>(["user"]);
  const clinicsData = queryClient.getQueryData<HealthcareCompany[]>([
    "healthcare-companies",
    HealthcareCompanyType.DENTAL_CLINIC,
  ]);
  const clinics = useMemo(
    () => clinicsData ?? EMPTY_CLINICS,
    [clinicsData]
  );

  // Prepare organization menu items
  const organizationMenuItems: MenuAction[] = useMemo(() => {
    const lastVisitedOrgId = getLocalStorage("__LAST_VISITED_C_ID");
    
    // Sort clinics alphabetically by name
    const sortedClinics = [...clinics].sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    // Separate the last visited organization and the remaining sorted clinics
    const lastVisitedOrg = sortedClinics.find(
      (clinic) => clinic.id === lastVisitedOrgId
    );
    const clinicItems = (lastVisitedOrg ? [lastVisitedOrg] : []).concat(
      sortedClinics.filter((clinic) => clinic.id !== lastVisitedOrgId).slice(0, 5)
    );

    // Map the selected clinics into menu items
    return clinicItems.map((c) => {
      // Create a stable reference for the icon function
      const IconComponent = () => (
        <Avatar
          initials={c.avatar.initials}
          name={c.name}
          source={c.avatar.image}
        />
      );
      
      return {
        content: c.name,
        icon: IconComponent,
        onAction: () => {
          setLocalStorage("__LAST_VISITED_C_ID", c.id);
          navigate(ROOTS.CLINIC + "/" + c.id);
        },
      };
    });
  }, [clinics, navigate]);

  return (
    <MedenticTopBar
      onNavigationToggle={onNavigationToggle}
      userMenuName={`${user?.firstName} ${user?.lastName}`}
      userMenuDetail={user?.email}
      userMenuInitials={user?.avatar?.initials}
      userMenuAvatar={user?.avatar?.image}
      organizationMenuItems={organizationMenuItems}
      allOrganizationsLabel={t("topbar.clinics")}
      allOrganizationsRoute={ROOTS.CLINIC}
      showSearchField={false}
      showLocaleSwitcher={false}
      showPIIToggle={true}
      showUserProfileInMenu={false}
    />
  );
};

export default React.memo(UserTopBar);
